import React, { useContext } from 'react';
import { AbilityContext } from '../../../components/ability/can';
import { CollapseFilterWrapper } from '../../../components/sidebar-filters/CollapseFilterWrapper';
import { ClosedSiteFilterSelect } from '../../../components/sidebar-filters/filter-select/ClosedSiteFilterSelect';
import { ControlProviderFilterSelect } from '../../../components/sidebar-filters/filter-select/ControlProviderFilterSelect';
import { DispatchTypeFilterSelect } from '../../../components/sidebar-filters/filter-select/DispatchTypeFilterSelect';
import { HasVenFilterSelect } from '../../../components/sidebar-filters/filter-select/HasVenFilterSelect';
import { ResourceTelemetryStatusFilterSelect } from '../../../components/sidebar-filters/filter-select/ResourceTelemetryStatusFilterSelect';
import { IntervalDataConnectionFilterSelect } from '../../../components/sidebar-filters/filter-select/IntervalDataConnectionFilterSelect';
import { MarketFilterSelect } from '../../../components/sidebar-filters/filter-select/MarketFilterSelect';
import { MeterEnrollmentFilterSelect } from '../../../components/sidebar-filters/filter-select/MeterEnrollmentFilterSelect';
import { ProgramFilterSelect } from '../../../components/sidebar-filters/filter-select/ProgramFilterSelect';
import { SiteStatusFilterSelect } from '../../../components/sidebar-filters/filter-select/SiteStatusFilterSelect';
import { UtilityCustomerFilterSelect } from '../../../components/sidebar-filters/filter-select/UtilityCustomerFilterSelect';
import { UtilityFilterSelect } from '../../../components/sidebar-filters/filter-select/UtilityFilterSelect';
import { useAuth } from '../../../domain/auth/useAuth';
import { SiteFilterKeys, SiteFilterLabels } from '../../../domain/saved-filters/interface';
import { useSidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { ISitesSidebarFilters, useSitesSidebarFiltersContext } from './SitesSidebarFiltersContext';
import { MeterFilterSelect } from '../../../components/sidebar-filters/filter-select/MeterFilterSelect';

interface ISitesSidebarFiltersProps {
    onFilterChange: (siteFilters: ISitesSidebarFilters) => void;
}

export const SitesSidebarFilters: React.FC<ISitesSidebarFiltersProps> = ({ onFilterChange }) => {
    const user = useAuth()!.user!;
    const ability = useContext(AbilityContext);
    const isAdminRole = user.isAdminRoleType();
    const isControlProviderRole = user.isControlProvider();

    const { siteFilters, handleSiteFilterChange: onSiteFilterChange } = useSitesSidebarFiltersContext();
    const {
        utilityOptions,
        controlProviderOptions,
        isLoading: isFiltersLoading,
        marketOptions,
        programOptions,
        siteStatusOptions,
        dispatchTypeOptions,
        meterEnrollmentOptions,
        meterOptions,
        hasVenOptions,
        resourceTelemetryStatusOptions,
        utilityCustomerOptions,
        siteIntervalDataOptions,
        closedSitesOptions,
    } = useSidebarFilterOptionsContext();

    const handleSiteFilterChange = (key: string, value: unknown) => {
        onSiteFilterChange(key, value);
        onFilterChange({ ...siteFilters, [key]: value });
    };

    const filtersItems = [
        ability.can('read', 'ControlProvider') && (
            <ControlProviderFilterSelect
                key={SiteFilterKeys.controlProviderId}
                label={SiteFilterLabels[SiteFilterKeys.controlProviderId]}
                value={siteFilters[SiteFilterKeys.controlProviderId] || []}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.controlProviderId, value)}
                options={controlProviderOptions}
                isLoading={isFiltersLoading}
            />
        ),
        ability.can('read', 'UtilityCustomer') && (
            <UtilityCustomerFilterSelect
                key={SiteFilterKeys.utilityCustomerId}
                label={SiteFilterLabels[SiteFilterKeys.utilityCustomerId]}
                value={siteFilters[SiteFilterKeys.utilityCustomerId] || []}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.utilityCustomerId, value)}
                options={utilityCustomerOptions}
                isLoading={isFiltersLoading}
            />
        ),
        <MarketFilterSelect
            key={SiteFilterKeys.market}
            label={SiteFilterLabels[SiteFilterKeys.market]}
            value={siteFilters[SiteFilterKeys.market] || []}
            onChange={value => handleSiteFilterChange(SiteFilterKeys.market, value)}
            options={marketOptions}
            isLoading={isFiltersLoading}
        />,
        <ProgramFilterSelect
            key={SiteFilterKeys.programId}
            label={SiteFilterLabels[SiteFilterKeys.programId]}
            value={siteFilters[SiteFilterKeys.programId] || []}
            onChange={value => handleSiteFilterChange(SiteFilterKeys.programId, value)}
            options={programOptions}
            isLoading={isFiltersLoading}
        />,
        isAdminRole && (
            <SiteStatusFilterSelect
                key={SiteFilterKeys.status}
                label={SiteFilterLabels[SiteFilterKeys.status]}
                value={siteFilters[SiteFilterKeys.status] || []}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.status, value)}
                options={siteStatusOptions}
            />
        ),
        <UtilityFilterSelect
            key={SiteFilterKeys.utility}
            label={SiteFilterLabels[SiteFilterKeys.utility]}
            value={siteFilters[SiteFilterKeys.utility] || []}
            onChange={value => handleSiteFilterChange(SiteFilterKeys.utility, value)}
            options={utilityOptions}
            isLoading={isFiltersLoading}
        />,
    ];

    const optionsItems = [
        ability.can('close-open', 'Site') && (
            <ClosedSiteFilterSelect
                key={SiteFilterKeys.isClosed}
                label={SiteFilterLabels[SiteFilterKeys.isClosed]}
                value={siteFilters[SiteFilterKeys.isClosed] || null}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.isClosed, value)}
                options={closedSitesOptions}
            />
        ),
        <DispatchTypeFilterSelect
            key={SiteFilterKeys.dispatchType}
            label={SiteFilterLabels[SiteFilterKeys.dispatchType]}
            value={siteFilters[SiteFilterKeys.dispatchType] || null}
            onChange={value => handleSiteFilterChange(SiteFilterKeys.dispatchType, value)}
            options={dispatchTypeOptions}
        />,
        isAdminRole && (
            <HasVenFilterSelect
                key={SiteFilterKeys.hasVen}
                label={SiteFilterLabels[SiteFilterKeys.hasVen]}
                value={siteFilters[SiteFilterKeys.hasVen] || null}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.hasVen, value)}
                options={hasVenOptions}
            />
        ),
        isAdminRole && (
            <ResourceTelemetryStatusFilterSelect
                key={SiteFilterKeys.resourceTelemetryStatus}
                label={SiteFilterLabels[SiteFilterKeys.resourceTelemetryStatus]}
                value={siteFilters[SiteFilterKeys.resourceTelemetryStatus] || null}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.resourceTelemetryStatus, value)}
                options={resourceTelemetryStatusOptions}
            />
        ),
        (isAdminRole || isControlProviderRole) && (
            <IntervalDataConnectionFilterSelect
                key={SiteFilterKeys.intervalData}
                label={SiteFilterLabels[SiteFilterKeys.intervalData]}
                value={siteFilters[SiteFilterKeys.intervalData] || null}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.intervalData, value)}
                options={siteIntervalDataOptions}
            />
        ),
        isAdminRole && (
            <MeterEnrollmentFilterSelect
                key={SiteFilterKeys.existSalesforceMeterEnrollments}
                label={SiteFilterLabels[SiteFilterKeys.existSalesforceMeterEnrollments]}
                value={siteFilters[SiteFilterKeys.existSalesforceMeterEnrollments] || null}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.existSalesforceMeterEnrollments, value)}
                options={meterEnrollmentOptions}
            />
        ),
        isAdminRole && (
            <MeterFilterSelect
                key={SiteFilterKeys.existSalesforceMeters}
                label={SiteFilterLabels[SiteFilterKeys.existSalesforceMeters]}
                value={siteFilters[SiteFilterKeys.existSalesforceMeters] || null}
                onChange={value => handleSiteFilterChange(SiteFilterKeys.existSalesforceMeters, value)}
                options={meterOptions}
            />
        ),
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', overflowY: 'auto' }}>
            {/* Filters */}
            <CollapseFilterWrapper label="Filters">{filtersItems}</CollapseFilterWrapper>

            {/* Options */}
            {optionsItems.length > 0 && <CollapseFilterWrapper label="Options">{optionsItems}</CollapseFilterWrapper>}
        </div>
    );
};
